import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  useFilterCreate,
  useFilterDelete,
  useFilterList,
  useFilterReorder,
  useFilterUpdate,
  useTabFilters,
  useUser,
} from '@/hooks';
import { filterKeys } from '@/hooks/useFilterQueries';
import { getAxiosErrorMessage } from '@/utils/error-utils';
import { ownerScopeToInputScope } from '@/lib/owner-scope';
import { ContentType, Filter, FilterDefinition, FilterScope } from '@vivport/backend/types';
import { type SortBy, Work4MeFiltersToolbar } from '@/components/work4me/Work4MeFiltersToolbar';
import { FilterListItem } from '@/components/work4me/FilterListItem';
import { ObligationRoleIcon } from '@/components/widget/obligation-role-icons';
import { Plus } from 'lucide-react';

const contentTypeOptions: { value: ContentType; label: string }[] = [
  { value: 'ANCHOR', label: 'ANCHOR' },
  { value: 'EVENT', label: 'EVENT' },
  { value: 'PHOTO', label: 'Photo' },
  { value: 'RECORDING', label: 'Recording' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'DOCUMENT', label: 'DOCUMENT' },
  { value: 'TEMPLATE', label: 'Template' },
];

type ObligationDef = NonNullable<FilterDefinition['obligations']>;
type ObligationRole = NonNullable<ObligationDef['role']>;
type PersonalPriorityChoice = 'ANY' | 'YES' | 'NO';
type ObligationFormState = {
  kinds: NonNullable<ObligationDef['kinds']>;
  statuses: NonNullable<ObligationDef['statuses']>;
  outcomes: NonNullable<ObligationDef['outcomes']>;
  role: ObligationRole;
  overdue: boolean;
  hasDueDate: boolean;
  dueBefore: string;
  dueAfter: string;
  hasPersonalPriority: PersonalPriorityChoice;
  personalPriorityAtMost: string;
};

const EMPTY_OBLIGATIONS: ObligationFormState = {
  kinds: [],
  statuses: [],
  outcomes: [],
  role: 'ANY',
  overdue: false,
  hasDueDate: false,
  dueBefore: '',
  dueAfter: '',
  hasPersonalPriority: 'ANY',
  personalPriorityAtMost: '',
};

const KIND_OPTIONS = ['DECISION', 'FAULT', 'ACTION', 'QUESTION', 'RISK'] as const satisfies readonly NonNullable<ObligationDef['kinds']>[number][];
const STATUS_OPTIONS = ['OPEN', 'CLARIFICATION', 'RESOLVED', 'CANCELLED'] as const satisfies readonly NonNullable<ObligationDef['statuses']>[number][];
const OUTCOME_OPTIONS = ['AFFIRMED', 'DECLINED', 'MITIGATED', 'ACCEPTED'] as const satisfies readonly NonNullable<ObligationDef['outcomes']>[number][];
const ROLE_OPTIONS: { value: ObligationRole; label: string }[] = [
  { value: 'ANY', label: 'Any' },
  { value: 'ACTIONEE', label: 'On my plate' },
  { value: 'ORIGINATOR', label: 'Waiting on others' },
  { value: 'INVOLVED', label: 'On my plate + waiting on others' },
  { value: 'UNASSIGNED', label: 'Unassigned' },
];

function buildObligationsValue(s: ObligationFormState): FilterDefinition['obligations'] | undefined {
  const o: ObligationDef = {};
  if (s.kinds.length) o.kinds = s.kinds;
  if (s.statuses.length) o.statuses = s.statuses;
  if (s.outcomes.length) o.outcomes = s.outcomes;
  if (s.role !== 'ANY') o.role = s.role;
  if (s.overdue) o.overdue = true;
  if (s.hasDueDate) o.hasDueDate = true;
  if (s.dueBefore) o.dueBefore = new Date(`${s.dueBefore}T23:59:59.999`).toISOString();
  if (s.dueAfter) o.dueAfter = new Date(`${s.dueAfter}T00:00:00.000`).toISOString();
  if (s.hasPersonalPriority === 'YES') o.hasPersonalPriority = true;
  if (s.hasPersonalPriority === 'NO') o.hasPersonalPriority = false;
  // personalPriorityAtMost implies a set priority; the backend rejects it combined
  // with hasPersonalPriority:false, so it is ignored under NO (input disabled too).
  if (s.hasPersonalPriority !== 'NO' && s.personalPriorityAtMost) {
    o.personalPriorityAtMost = Number(s.personalPriorityAtMost);
  }
  return Object.keys(o).length ? o : undefined;
}

function obligationsToFormState(o: FilterDefinition['obligations']): ObligationFormState {
  if (!o) return EMPTY_OBLIGATIONS;
  return {
    kinds: o.kinds ?? [],
    statuses: o.statuses ?? [],
    outcomes: o.outcomes ?? [],
    role: o.role ?? 'ANY',
    overdue: o.overdue ?? false,
    hasDueDate: o.hasDueDate ?? false,
    dueBefore: o.dueBefore ? o.dueBefore.slice(0, 10) : '',
    dueAfter: o.dueAfter ? o.dueAfter.slice(0, 10) : '',
    hasPersonalPriority: o.hasPersonalPriority === true ? 'YES' : o.hasPersonalPriority === false ? 'NO' : 'ANY',
    personalPriorityAtMost: o.personalPriorityAtMost !== undefined ? String(o.personalPriorityAtMost) : '',
  };
}

function getMutationErrorMessage(error: unknown, fallback: string): string {
  const apiMessage = getAxiosErrorMessage(error);
  if (apiMessage?.trim()) return apiMessage;
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

function filterBySearch(items: Filter[], searchTerm: string, getLabel: (f: Filter) => string): Filter[] {
  const normalized = searchTerm.toLowerCase().trim();
  if (!normalized) return items;
  return items.filter((f) => getLabel(f).toLowerCase().includes(normalized));
}

function sortFiltersByMode(filters: Filter[], sortBy: SortBy): Filter[] {
  if (sortBy === 'sort-order') return filters;
  return [...filters].sort((a, b) => {
    if (sortBy === 'name-asc') return a.name.localeCompare(b.name);
    if (sortBy === 'name-desc') return b.name.localeCompare(a.name);
    if (sortBy === 'updated-asc') return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

function canManageFilter(filter: Filter, selectedMemberId?: number, canManageHubScope?: boolean): boolean {
  if (!selectedMemberId) return false;
  if (filter.ownerScope === 'MEMBER') return filter.ownerMemberId === selectedMemberId;
  return !!canManageHubScope;
}

function getTabLabel(f: Filter): string {
  const def = f.definition as { tabLabel?: string };
  return def?.tabLabel ?? f.name;
}

export function Work4MeFilterList() {
  const { data: memberContext } = useUser();
  const memberId = memberContext?.selectedMember.id;
  const canManageHubScope = memberContext?.isHubAdmin ?? false;

  const filterListQuery = useFilterList();
  const tabFiltersQuery = useTabFilters(memberId, memberContext?.selectedMember.hubId);
  const createFilterMutation = useFilterCreate();
  const updateFilterMutation = useFilterUpdate();
  const deleteFilterMutation = useFilterDelete();
  const queryClient = useQueryClient();
  const reorderMutation = useFilterReorder();

  const [filterSearch, setFilterSearch] = useState('');
  const [filterSortBy, setFilterSortBy] = useState<SortBy>('sort-order');
  const [tabSearch, setTabSearch] = useState('');
  const [tabSortBy, setTabSortBy] = useState<SortBy>('sort-order');

  // --- Filter dialog state ---
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterDialogMode, setFilterDialogMode] = useState<'create' | 'edit'>('create');
  const [editingFilter, setEditingFilter] = useState<Filter | null>(null);
  const [filterName, setFilterName] = useState('');
  const [filterDescription, setFilterDescription] = useState('');
  const [filterScope, setFilterScope] = useState<FilterScope>('PRIVATE');
  const [filterContentTypes, setFilterContentTypes] = useState<ContentType[]>([]);
  const [filterObligations, setFilterObligations] = useState<ObligationFormState>(EMPTY_OBLIGATIONS);

  // --- Tab dialog state ---
  const [tabDialogOpen, setTabDialogOpen] = useState(false);
  const [editingTab, setEditingTab] = useState<Filter | null>(null);
  const [tabName, setTabName] = useState('');
  const [tabDescription, setTabDescription] = useState('');
  const [tabContentTypes, setTabContentTypes] = useState<ContentType[]>([]);

  // --- Delete Filter dialog state ---
  const [deleteFilterDialogOpen, setDeleteFilterDialogOpen] = useState(false);
  const [pendingFilterDelete, setPendingFilterDelete] = useState<Filter | null>(null);

  // --- Delete Tab dialog state ---
  const [deleteTabDialogOpen, setDeleteTabDialogOpen] = useState(false);
  const [pendingTabDelete, setPendingTabDelete] = useState<Filter | null>(null);

  const isSubmitting = createFilterMutation.isPending || updateFilterMutation.isPending;

  // --- Data ---
  const filterIds = filterListQuery.data?.ids ?? [];
  const savedFilters = useMemo(() => {
    return filterIds
      .map((fid) => queryClient.getQueryData(filterKeys.detail(fid)) as Filter | undefined)
      .filter((f): f is Filter => !!f);
  }, [filterIds, queryClient, filterListQuery.dataUpdatedAt]);
  const tabFilters = tabFiltersQuery.data ?? [];

  const visibleFilters = useMemo(() => {
    const searched = filterBySearch(savedFilters, filterSearch, (f) => f.name);
    return sortFiltersByMode(searched, filterSortBy);
  }, [savedFilters, filterSearch, filterSortBy]);

  const visibleTabs = useMemo(
    () => filterBySearch(tabFilters, tabSearch, getTabLabel),
    [tabFilters, tabSearch]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleReorder = useCallback(
    (payload: { tabIds: number[]; filterIds: number[] }) => {
      reorderMutation.mutate(payload, {
        onError: () => {
          toast.error('Unable to save order. Please try again.');
          queryClient.invalidateQueries({ queryKey: filterKeys.lists() });
          queryClient.invalidateQueries({ queryKey: [...filterKeys.all, 'tabs'] });
        },
      });
    },
    [reorderMutation, queryClient]
  );

  const handleFilterDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const parseId = (s: string) => parseInt(String(s).replace('filter-', ''), 10);
      const activeId = parseId(String(active.id));
      const overId = parseId(String(over.id));
      const fromIdx = visibleFilters.findIndex((f) => f.id === activeId);
      const toIdx = visibleFilters.findIndex((f) => f.id === overId);
      if (fromIdx === -1 || toIdx === -1) return;
      const reordered = arrayMove(visibleFilters.map((f) => f.id), fromIdx, toIdx);
      handleReorder({ tabIds: tabFilters.map((t) => t.id), filterIds: reordered });
    },
    [visibleFilters, tabFilters, handleReorder]
  );

  const handleTabDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const parseId = (s: string) => parseInt(String(s).replace('tab-', ''), 10);
      const activeId = parseId(String(active.id));
      const overId = parseId(String(over.id));
      const fromIdx = visibleTabs.findIndex((t) => t.id === activeId);
      const toIdx = visibleTabs.findIndex((t) => t.id === overId);
      if (fromIdx === -1 || toIdx === -1) return;
      const reordered = arrayMove(visibleTabs.map((t) => t.id), fromIdx, toIdx);
      handleReorder({ tabIds: reordered, filterIds: savedFilters.map((f) => f.id) });
    },
    [visibleTabs, savedFilters, handleReorder]
  );

  // --- Filter CRUD handlers ---
  const openCreateFilterDialog = () => {
    setFilterDialogMode('create');
    setEditingFilter(null);
    setFilterName('');
    setFilterDescription('');
    setFilterScope('PRIVATE');
    setFilterContentTypes([]);
    setFilterObligations(EMPTY_OBLIGATIONS);
    setFilterDialogOpen(true);
  };

  const openEditFilterDialog = (filter: Filter) => {
    setFilterDialogMode('edit');
    setEditingFilter(filter);
    setFilterName(filter.name);
    setFilterDescription(filter.description ?? '');
    setFilterScope(ownerScopeToInputScope(filter.ownerScope));
    setFilterContentTypes(filter.definition.contentTypes ?? []);
    setFilterObligations(obligationsToFormState(filter.definition.obligations));
    setFilterDialogOpen(true);
  };

  const handleFilterSubmit = () => {
    const trimmed = filterName.trim();
    if (!trimmed) return;

    if (filterDialogMode === 'create') {
      if (filterScope === 'HUB' && !canManageHubScope) {
        toast.error('HUB-scope filters require organization admin access.');
        return;
      }
      createFilterMutation.mutate(
        {
          name: trimmed,
          description: filterDescription.trim() || undefined,
          scope: filterScope,
          definition: {
            projection: 'CONTENT',
            contentTypes: filterContentTypes.length ? filterContentTypes : undefined,
            obligations: buildObligationsValue(filterObligations),
          },
        },
        {
          onSuccess: () => setFilterDialogOpen(false),
          onError: (error) => toast.error(getMutationErrorMessage(error, 'Unable to create filter.')),
        }
      );
      return;
    }

    if (!editingFilter) return;
    updateFilterMutation.mutate(
      {
        id: editingFilter.id,
        data: {
          name: trimmed,
          description: filterDescription.trim() || undefined,
          definition: {
            ...editingFilter.definition,
            contentTypes: filterContentTypes.length ? filterContentTypes : undefined,
            obligations: buildObligationsValue(filterObligations),
          },
        },
      },
      {
        onSuccess: () => setFilterDialogOpen(false),
        onError: (error) => toast.error(getMutationErrorMessage(error, 'Unable to update filter.')),
      }
    );
  };

  // --- Tab edit handler ---
  const openEditTabDialog = (tab: Filter) => {
    setEditingTab(tab);
    const def = tab.definition as { tabLabel?: string };
    setTabName(def?.tabLabel ?? tab.name);
    setTabDescription(tab.description ?? '');
    setTabContentTypes(tab.definition.contentTypes ?? []);
    setTabDialogOpen(true);
  };

  const handleTabSubmit = () => {
    const trimmed = tabName.trim();
    if (!trimmed || !editingTab) return;
    updateFilterMutation.mutate(
      {
        id: editingTab.id,
        data: {
          name: trimmed,
          description: tabDescription.trim() || undefined,
          definition: {
            ...editingTab.definition,
            contentTypes: tabContentTypes.length ? tabContentTypes : undefined,
            tabLabel: trimmed,
            tabLabelMobile: trimmed,
          },
        },
      },
      {
        onSuccess: () => setTabDialogOpen(false),
        onError: (error) => toast.error(getMutationErrorMessage(error, 'Unable to update tab.')),
      }
    );
  };

  // --- Delete Filter handler ---
  const openDeleteFilterDialog = (filter: Filter) => {
    setPendingFilterDelete(filter);
    setDeleteFilterDialogOpen(true);
  };

  const handleDeleteFilter = () => {
    if (!pendingFilterDelete) return;
    deleteFilterMutation.mutate(pendingFilterDelete.id, {
      onSuccess: () => {
        setDeleteFilterDialogOpen(false);
        setPendingFilterDelete(null);
      },
      onError: (error) => toast.error(getMutationErrorMessage(error, 'Unable to delete filter.')),
    });
  };

  // --- Delete Tab handler ---
  const openDeleteTabDialog = (tab: Filter) => {
    setPendingTabDelete(tab);
    setDeleteTabDialogOpen(true);
  };

  const handleDeleteTab = () => {
    if (!pendingTabDelete) return;
    deleteFilterMutation.mutate(pendingTabDelete.id, {
      onSuccess: () => {
        setDeleteTabDialogOpen(false);
        setPendingTabDelete(null);
      },
      onError: (error) => toast.error(getMutationErrorMessage(error, 'Unable to remove tab.')),
    });
  };

  const isLoading = filterListQuery.isLoading || tabFiltersQuery.isLoading;
  const isError = filterListQuery.isError || tabFiltersQuery.isError;
  const canDragFilters = !filterSearch.trim() && visibleFilters.length > 1;
  const canDragTabs = !tabSearch.trim() && visibleTabs.length > 1;

  return (
    <div className="-mx-4 py-2">
      {/* Saved Filters section */}
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">Saved Filters</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Work4MeFiltersToolbar search={filterSearch} onSearchChange={setFilterSearch} sortBy={filterSortBy} onSortChange={setFilterSortBy} />
              <Button onClick={openCreateFilterDialog} className="gap-2 h-9 px-3">
                <Plus className="h-4 w-4" />
                Filter
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground">Manage saved filters for your content views.</p>
        </div>
      </div>

      {isLoading ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">Loading...</div>
      ) : isError ? (
        <div className="px-4 py-6 text-sm text-destructive">Unable to load filters.</div>
      ) : visibleFilters.length === 0 ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">
          {filterSearch.trim() ? 'No filters match your search.' : 'No saved filters yet.'}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleFilterDragEnd}>
          <SortableContext items={visibleFilters.map((f) => `filter-${f.id}`)} strategy={verticalListSortingStrategy}>
            <ul className="divide-y">
              {visibleFilters.map((filter) => {
                const canManage = canManageFilter(filter, memberId, canManageHubScope);
                return (
                  <FilterListItem
                    key={filter.id}
                    filter={filter}
                    canEdit={canManage && !reorderMutation.isPending}
                    canDelete={canManage && !reorderMutation.isPending}
                    isDraggable={canDragFilters && canManage && !reorderMutation.isPending}
                    onEdit={openEditFilterDialog}
                    onDelete={openDeleteFilterDialog}
                  />
                );
              })}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {/* Explore Tabs section */}
      <div className="px-4 py-3 border-b border-t bg-muted/20 mt-6">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">Explore Tabs</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Work4MeFiltersToolbar search={tabSearch} onSearchChange={setTabSearch} sortBy={tabSortBy} onSortChange={setTabSortBy} />
            </div>
          </div>
          <p className="text-muted-foreground">Tabs appear in the Explore tab bar. Spawn new tabs from the Explore view.</p>
        </div>
      </div>

      {isLoading ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">Loading...</div>
      ) : isError ? (
        <div className="px-4 py-6 text-sm text-destructive">Unable to load tabs.</div>
      ) : visibleTabs.length === 0 ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">
          {tabSearch.trim() ? 'No tabs match your search.' : 'No explore tabs yet.'}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleTabDragEnd}>
          <SortableContext items={visibleTabs.map((t) => `tab-${t.id}`)} strategy={verticalListSortingStrategy}>
            <ul className="divide-y">
              {visibleTabs.map((tab) => {
                const canManage = canManageFilter(tab, memberId, canManageHubScope);
                return (
                  <FilterListItem
                    key={tab.id}
                    filter={tab}
                    sortableId={`tab-${tab.id}`}
                    canEdit={canManage && !reorderMutation.isPending}
                    canDelete={canManage && !reorderMutation.isPending}
                    isDraggable={canDragTabs && canManage && !reorderMutation.isPending}
                    onEdit={openEditTabDialog}
                    onDelete={openDeleteTabDialog}
                  />
                );
              })}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {/* Filter create/edit dialog */}
      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{filterDialogMode === 'create' ? 'Create Filter' : 'Edit Filter'}</DialogTitle>
            <DialogDescription>
              {filterDialogMode === 'create' ? 'Create a saved filter for your content.' : 'Update filter details and content type scope.'}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="filter-name">Name</Label>
              <Input id="filter-name" value={filterName} onChange={(e) => setFilterName(e.target.value)} placeholder="Enter a name..." />
            </div>
            <div className="space-y-2">
              <Label htmlFor="filter-description">Description (optional)</Label>
              <Textarea id="filter-description" value={filterDescription} onChange={(e) => setFilterDescription(e.target.value)} placeholder="Add a description..." rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Scope</Label>
              <Select value={filterScope} onValueChange={(v) => setFilterScope(v as FilterScope)} disabled={filterDialogMode === 'edit'}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PRIVATE">PRIVATE</SelectItem>
                  <SelectItem value="HUB" disabled={!canManageHubScope}>HUB {canManageHubScope ? '' : '(hub admin only)'}</SelectItem>
                </SelectContent>
              </Select>
              {filterDialogMode === 'edit' && (
                <p className="text-xs text-muted-foreground">Scope cannot be changed after creation.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Content Types</Label>
              <div className="grid grid-cols-2 gap-2">
                {contentTypeOptions.map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={filterContentTypes.includes(opt.value) ? 'default' : 'outline'}
                    onClick={() => setFilterContentTypes((prev) => prev.includes(opt.value) ? prev.filter((v) => v !== opt.value) : [...prev, opt.value])}
                    className="justify-start"
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Leave all unselected to include all content types.</p>
            </div>
            <div className="space-y-3 border-t pt-4">
              <div className="space-y-1">
                <Label>Obligations</Label>
                <p className="text-xs text-muted-foreground">
                  Narrow to Content with an attached obligation. Leave blank to ignore obligations.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Kinds</Label>
                <div className="grid grid-cols-2 gap-2">
                  {KIND_OPTIONS.map((kind) => (
                    <Button
                      key={kind}
                      type="button"
                      size="sm"
                      variant={filterObligations.kinds.includes(kind) ? 'default' : 'outline'}
                      onClick={() => setFilterObligations((prev) => ({
                        ...prev,
                        kinds: prev.kinds.includes(kind) ? prev.kinds.filter((v) => v !== kind) : [...prev.kinds, kind],
                      }))}
                      className="justify-start"
                    >
                      {kind}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Statuses</Label>
                <div className="grid grid-cols-2 gap-2">
                  {STATUS_OPTIONS.map((status) => (
                    <Button
                      key={status}
                      type="button"
                      size="sm"
                      variant={filterObligations.statuses.includes(status) ? 'default' : 'outline'}
                      onClick={() => setFilterObligations((prev) => ({
                        ...prev,
                        statuses: prev.statuses.includes(status) ? prev.statuses.filter((v) => v !== status) : [...prev.statuses, status],
                      }))}
                      className="justify-start"
                    >
                      {status}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  None selected = open obligations (OPEN, CLARIFICATION), matching the row badge.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Outcomes</Label>
                <div className="grid grid-cols-2 gap-2">
                  {OUTCOME_OPTIONS.map((outcome) => (
                    <Button
                      key={outcome}
                      type="button"
                      size="sm"
                      variant={filterObligations.outcomes.includes(outcome) ? 'default' : 'outline'}
                      onClick={() => setFilterObligations((prev) => ({
                        ...prev,
                        outcomes: prev.outcomes.includes(outcome) ? prev.outcomes.filter((v) => v !== outcome) : [...prev.outcomes, outcome],
                      }))}
                      className="justify-start"
                    >
                      {outcome}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Role</Label>
                <Select
                  value={filterObligations.role}
                  onValueChange={(v) => setFilterObligations((prev) => ({ ...prev, role: v as ObligationRole }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className="flex items-center gap-2">
                          {(opt.value === 'ACTIONEE' || opt.value === 'ORIGINATOR') && (
                            <ObligationRoleIcon role={opt.value} />
                          )}
                          {opt.value === 'INVOLVED' && (
                            <span className="flex items-center gap-0.5">
                              <ObligationRoleIcon role="ACTIONEE" />
                              <ObligationRoleIcon role="ORIGINATOR" />
                            </span>
                          )}
                          {opt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">My priority</Label>
                <p className="text-xs text-muted-foreground">
                  Matches each viewing member&apos;s own priorities, so shared filters give per-member results.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Has my priority</Label>
                    <Select
                      value={filterObligations.hasPersonalPriority}
                      onValueChange={(v) => setFilterObligations((prev) => ({ ...prev, hasPersonalPriority: v as PersonalPriorityChoice }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ANY">Any</SelectItem>
                        <SelectItem value="YES">Yes</SelectItem>
                        <SelectItem value="NO">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="obligation-priority-at-most" className="text-xs text-muted-foreground">My priority at most (1-10)</Label>
                    <Input
                      id="obligation-priority-at-most"
                      type="number"
                      min={1}
                      max={10}
                      value={filterObligations.personalPriorityAtMost}
                      disabled={filterObligations.hasPersonalPriority === 'NO'}
                      onChange={(e) => setFilterObligations((prev) => ({ ...prev, personalPriorityAtMost: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Due</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={filterObligations.overdue ? 'default' : 'outline'}
                    onClick={() => setFilterObligations((prev) => ({ ...prev, overdue: !prev.overdue }))}
                    className="justify-start"
                  >
                    Overdue only
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={filterObligations.hasDueDate ? 'default' : 'outline'}
                    onClick={() => setFilterObligations((prev) => ({ ...prev, hasDueDate: !prev.hasDueDate }))}
                    className="justify-start"
                  >
                    Has due date
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="obligation-due-after" className="text-xs text-muted-foreground">Due after</Label>
                    <Input
                      id="obligation-due-after"
                      type="date"
                      value={filterObligations.dueAfter}
                      onChange={(e) => setFilterObligations((prev) => ({ ...prev, dueAfter: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="obligation-due-before" className="text-xs text-muted-foreground">Due before</Label>
                    <Input
                      id="obligation-due-before"
                      type="date"
                      value={filterObligations.dueBefore}
                      onChange={(e) => setFilterObligations((prev) => ({ ...prev, dueBefore: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleFilterSubmit} disabled={!filterName.trim() || isSubmitting}>
              {filterDialogMode === 'create' ? 'Create' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tab edit dialog */}
      <Dialog open={tabDialogOpen} onOpenChange={setTabDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tab</DialogTitle>
            <DialogDescription>Edit the tab name and which content types it shows.</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tab-name">Name</Label>
              <Input id="tab-name" value={tabName} onChange={(e) => setTabName(e.target.value)} placeholder="Enter a name..." />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tab-description">Description (optional)</Label>
              <Textarea id="tab-description" value={tabDescription} onChange={(e) => setTabDescription(e.target.value)} placeholder="Add a description..." rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Content Types</Label>
              <div className="grid grid-cols-2 gap-2">
                {contentTypeOptions.map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={tabContentTypes.includes(opt.value) ? 'default' : 'outline'}
                    onClick={() => setTabContentTypes((prev) => prev.includes(opt.value) ? prev.filter((v) => v !== opt.value) : [...prev, opt.value])}
                    className="justify-start"
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Leave all unselected to include all content types.</p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTabDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleTabSubmit} disabled={!tabName.trim() || isSubmitting}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Filter dialog */}
      <Dialog open={deleteFilterDialogOpen} onOpenChange={setDeleteFilterDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Filter</DialogTitle>
            <DialogDescription>
              {`Delete filter "${pendingFilterDelete?.name}"? This action cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFilterDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteFilter} disabled={deleteFilterMutation.isPending || reorderMutation.isPending}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Tab dialog */}
      <Dialog open={deleteTabDialogOpen} onOpenChange={setDeleteTabDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Tab</DialogTitle>
            <DialogDescription>
              {`Remove tab "${(pendingTabDelete?.definition as { tabLabel?: string } | undefined)?.tabLabel ?? pendingTabDelete?.name}"? It will no longer appear in your Explore tab bar. This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTabDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteTab} disabled={deleteFilterMutation.isPending || reorderMutation.isPending}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
